import { first, size } from "lodash"
import { formatValueForDisplay } from "./formatting"
import { VALUE_FORMATS } from "helpers/ai"
import { ARTIFACT_KINDS } from "connected-components/dashboard/modules/ai-artifact/types"
import type { Artifact, TableArtifact, PlotArtifact, ArtifactColumn } from "connected-components/dashboard/modules/ai-artifact/types"
import type { DownloadData } from "connected-components/dashboard/context/download-registry"

/** Minimal shape of a Plotly trace — only the fields accessed during download. */
interface PlotTrace {
  name?: string
  x?: unknown[]
  y?: unknown[]
  labels?: unknown[]
  values?: unknown[]
}

function toCanonicalKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "")
}

/**
 * Resolve a value from a record using accessor candidates.
 * Tries exact key match first, then canonical match (case-insensitive, ignores spaces/symbols).
 */
function getValueFromRecord(record: Record<string, unknown>, accessorCandidates: string[]): unknown {
  const filtered = accessorCandidates.filter(Boolean)

  const directMatch = filtered.find((key) => Object.hasOwn(record, key))
  if (directMatch) return record[directMatch]

  const canonicalCandidates = new Set(filtered.map(toCanonicalKey))
  if (canonicalCandidates.size === 0) return undefined

  return Object.entries(record).find(([recordKey]) => canonicalCandidates.has(toCanonicalKey(recordKey)))?.[1]
}

/**
 * Converts a table artifact to download format.
 * Uses column metadata for headers and accessors; formats values for display.
 */
function tableArtifactToDownloadData(artifact: TableArtifact, currency?: string): DownloadData | null {
  const { columns: rawColumns, records } = artifact
  const hasRecords = records?.length > 0
  const hasColumnMetadata = rawColumns?.length > 0
  const firstRecord = first(records)

  if (!hasRecords && !hasColumnMetadata) {
    return null
  }

  const columnAccessors: string[] = hasColumnMetadata
    ? rawColumns.map((col: ArtifactColumn) => col.id || col.field || col.key || col.name || col.label)
    : []

  const headers: string[] = hasColumnMetadata
    ? rawColumns.map((col: ArtifactColumn) => col.label)
    : firstRecord
      ? Object.keys(firstRecord)
      : []

  if (headers.length === 0) {
    return null
  }

  const rows = (records || []).map((record) =>
    headers.map((header, colIndex) => {
      const accessorCandidates = hasColumnMetadata
        ? [columnAccessors[colIndex], header]
        : [header]
      const value = getValueFromRecord(record, accessorCandidates)
      const format = (hasColumnMetadata && rawColumns[colIndex]?.format) || VALUE_FORMATS.STRING
      const formatted = formatValueForDisplay(value, format, currency)
      return formatted === null || formatted === "" ? "" : String(formatted)
    }),
  )

  return { headers, rows }
}

/**
 * Returns the x-axis values from a set of Plotly traces.
 * Tries `x` arrays first (scatter, bar, line), then `labels` (pie charts).
 */
function getTraceXValues(traces: PlotTrace[]): unknown[] | null {
  const traceWithX = traces.find((t) => Array.isArray(t.x) && t.x.length > 0)
  if (traceWithX?.x) return traceWithX.x

  const traceWithLabels = traces.find((t) => Array.isArray(t.labels) && t.labels.length > 0)
  return traceWithLabels?.labels ?? null
}

/** Returns true when a trace carries its own x-axis data (x array or pie labels). */
function traceHasOwnAxis(trace: PlotTrace): boolean {
  return size(trace.x) > 0 || size(trace.labels) > 0
}

/**
 * Returns the y-axis values from a single Plotly trace.
 * Tries `y` first, then `values` (pie charts).
 */
function getTraceYValues(trace: PlotTrace): unknown[] | null {
  if (Array.isArray(trace.y)) return trace.y
  if (Array.isArray(trace.values)) return trace.values
  return null
}

/**
 * Extracts tabular data from a Plotly figure for download.
 * Handles scatter/bar/line (x/y) and pie charts (labels/values).
 * Multiple traces become additional Y columns.
 */
function plotArtifactToDownloadData(artifact: PlotArtifact): DownloadData | null {
  const traces = artifact.figure?.data as PlotTrace[] | undefined
  if (!Array.isArray(traces) || traces.length === 0) {
    return null
  }

  const xValues = getTraceXValues(traces)
  if (!xValues || xValues.length === 0) {
    return null
  }

  const headers = ["X", ...traces.map((trace, i) => trace.name || `Y${i + 1}`)]

  // No-x traces must not extend the row count: their y-values beyond xValues.length
  // are unanchored and would produce all-empty trailing rows.
  const maxLen = Math.max(
    xValues.length,
    ...traces.filter(traceHasOwnAxis).map((trace) => getTraceYValues(trace)?.length ?? 0),
  )

  const rows: string[][] = []
  for (let i = 0; i < maxLen; i++) {
    rows.push([
      xValues[i] != null ? String(xValues[i]) : "",
      ...traces.map((trace) => {
        if (!traceHasOwnAxis(trace)) return ""

        const yValues = getTraceYValues(trace)
        const val = yValues?.[i]
        return val != null ? String(val) : ""
      }),
    ])
  }

  return { headers, rows }
}

/**
 * Converts an artifact to DownloadData based on its type.
 *
 * @param artifact - TableArtifact or PlotArtifact
 * @param currency - Optional currency for value formatting (e.g. from useResourceCurrency)
 * @returns DownloadData or null if the artifact has no exportable data
 */
export function artifactToDownloadData(artifact: Artifact | null | undefined, currency?: string): DownloadData | null {
  if (!artifact) return null

  if (artifact.type === ARTIFACT_KINDS.TABLE) {
    return tableArtifactToDownloadData(artifact, currency)
  }

  if (artifact.type === ARTIFACT_KINDS.PLOT) {
    return plotArtifactToDownloadData(artifact)
  }

  return null
}
